"use client";
import { motion } from "framer-motion";
import Link from "next/link";
import { ReactNode, useRef, useEffect } from "react";
import { useMotion } from "@/contexts/MotionContext";
import SafeImage from "@/components/ui/SafeImage";
import { CARD_TEXTURE_SURFACE } from "@/lib/styles/surfaces";

interface WorkCardProps {
  slug: string | null;
  title: string;
  description: string | ReactNode;
  color: string;
  client: {
    name: string;
    logo: string;
  };
  featuredMedia: {
    type: "image" | "video";
    url: string;
  };
  categories?: string[];
}

const CATEGORY_ACCENT_COLORS: Record<string, string> = {
  "connected-systems": "#F1C402",
  "digital-experiences": "#F6BBCD",
  "medical-devices": "#71D9C5",
};

export default function WorkCard({
  slug,
  title,
  description,
  color,
  client,
  featuredMedia,
  categories,
}: WorkCardProps) {
  const { isMotionEnabled } = useMotion();
  const videoRef = useRef<HTMLVideoElement>(null);
  const accentColor =
    (categories && categories.length > 0
      ? CATEGORY_ACCENT_COLORS[categories[0]]
      : undefined) ?? color;

  const showMediaTint = !(categories?.includes("medical-devices") ?? false);

  useEffect(() => {
    if (videoRef.current && !isMotionEnabled) {
      videoRef.current.currentTime = 0;
      videoRef.current.pause();
    }
  }, [isMotionEnabled]);

  const CardContent = () => (
    <div className="relative flex h-full min-h-0 flex-col p-6 text-text-light dark:text-text-dark">
      {/* Top Media */}
      <div className="relative mb-6 shrink-0">
        {showMediaTint ? (
          <div
            className="absolute inset-0 -m-2 rounded-lg"
            style={{ backgroundColor: accentColor }}
          />
        ) : null}
        <div className="relative aspect-video rounded-md overflow-hidden">
          {featuredMedia.type === "video" ? (
            <video
              ref={videoRef}
              src={featuredMedia.url}
              className="w-full h-full object-cover"
              autoPlay={isMotionEnabled}
              muted
              loop
              playsInline
            />
          ) : (
            <SafeImage
              src={featuredMedia.url}
              alt={title}
              fill
              className="object-cover"
            />
          )}
        </div>
      </div>

      {/* Content */}
      <div className="flex min-h-0 flex-1 flex-col gap-4">
        {/* Client Info */}
        <div className="flex shrink-0 items-center gap-3">
          <div className="w-10 h-10 rounded-full overflow-hidden bg-black/5 dark:bg-white/10">
            <SafeImage
              src={client.logo}
              alt={client.name}
              width={40}
              height={40}
              className="w-full h-full object-cover"
            />
          </div>
          <div>
            <h3 className="text-xl font-medium text-text-light dark:text-text-dark">
              {title}
            </h3>
            <p className="text-sm text-text-muted-light dark:text-text-muted-dark">
              {client.name}
            </p>
          </div>
        </div>

        {/* Description + CTA: flex-1 so “View Project” stays at the card bottom */}
        <div className="flex min-h-0 flex-1 flex-col gap-3">
          {typeof description === "string" ? (
            <p
              className="text-sm text-text-muted-light dark:text-text-muted-dark overflow-hidden"
              style={{
                display: "-webkit-box",
                WebkitLineClamp: 3,
                WebkitBoxOrient: "vertical",
              }}
            >
              {description}
            </p>
          ) : (
            <div
              className="text-sm text-text-muted-light dark:text-text-muted-dark overflow-hidden"
              style={{
                display: "-webkit-box",
                WebkitLineClamp: 3,
                WebkitBoxOrient: "vertical",
              }}
            >
              {description}
            </div>
          )}
          {slug && (
            <button
              type="button"
              className="mt-auto self-start px-4 py-2 text-sm border border-current rounded-full hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
            >
              View Project
            </button>
          )}
        </div>
      </div>
    </div>
  );

  const cardClasses = `relative flex h-full min-h-0 flex-col rounded-xl overflow-hidden ${CARD_TEXTURE_SURFACE}`;

  const Card = () => (
    <motion.div
      className={cardClasses}
      whileHover={{ scale: 1.01 }}
      transition={{ duration: 0.2 }}
    >
      <CardContent />
    </motion.div>
  );

  // Wrap in Link if there's a slug
  return slug ? (
    <Link href={`/work/${slug}`} className="block h-full min-h-0">
      <Card />
    </Link>
  ) : (
    <Card />
  );
}
